import { hashKey, } from "@tanstack/react-query";
// Keyed by QueryClient on purpose: `bootstrap()` creates exactly one per context id
// (bootstrap.tsx `build()`), so the client IS the dedupe boundary. WeakMap, not Map:
// every test file and every dynamically bootstrapped context would otherwise be
// retained forever by a module-level registry.
const dispatchWindows = new WeakMap();
/**
 * Collapses `invalidateQueries` calls that happen for the same `(QueryClient, refetchType,
 * queryKey)` within a single synchronous dispatch tick (e.g. several mounted hook instances
 * reacting to the same domain event) into exactly one call.
 *
 * The window is a single microtask: it opens on the first call and clears via
 * `queueMicrotask`, so a later tick (another domain event, another macrotask such as an
 * `invalidationDelay` timeout) always invalidates again. Instances using `invalidationDelay`
 * schedule their call in separate per-instance macrotasks — even when they share the same
 * delay value — and therefore fall outside this window — see the `invalidationDelay` JSDoc in
 * `useQuery`/`useInfiniteQuery`.
 */
export const invalidateQueriesOnce = ({ queryClient, queryKey, refetchType, }) => {
    // `hashKey` is used only for window identity here, never for cache lookup.
    const key = `${refetchType}::${hashKey(queryKey)}`;
    let window = dispatchWindows.get(queryClient);
    if (!window) {
        window = new Set();
        dispatchWindows.set(queryClient, window);
    }
    if (window.has(key))
        return;
    window.add(key);
    queueMicrotask(() => window.delete(key));
    void queryClient.invalidateQueries({ queryKey, refetchType });
};
